import type { Dashboard, DashboardSummary, DashboardTile } from '../../core/domain/dashboard';
import { catalogForSource } from '../../dashboards/catalog';
import type { BackendClient } from '../bridge/backend-client';
import type { StreamHealth } from './conn-state';
import type { StreamFrame } from '../../core/bridge/types';
import type { ShareTermsWire, ShareScopeWire, ShareListingWire } from '../../core/bridge/connect-client';

/*
 * DashboardManager - main-thread PROXY of the dashboard ROSTER.
 *
 * Same shape as CameraManager and ControllerManager: a local cache, a `change`
 * event, and components that read at render time rather than wiring their own
 * listeners. Anything that behaves differently here would be a second pattern
 * for no reason.
 *
 * The roster holds SUMMARIES, never bodies. A body is fetched only for the
 * dashboard being opened, because a fifty-tile dashboard carrying widget source
 * is megabytes and a list of them would be tens.
 *
 * WRITES GO FROM THIS BROWSER TO THE GATEWAY, not through the cloud, and that
 * is deliberate: an edge api-gateway is LAN-only and unreachable from
 * Cloudflare by design, so a cloud-side write would work against a
 * cloud-connected box and fail against a LAN-only one. A browser on the factory
 * floor reaches the box either way. See connect-client.ts for the long version
 * before "fixing" the absence of a server-side write.
 *
 * NO OPTIMISTIC UPDATES. A write returns and the roster subscription delivers
 * the new summary; showing the change before the gateway confirmed it would
 * make a refused save look like a successful one until the next reload.
 */
export class DashboardManager extends EventTarget {
  private cache = new Map<string, DashboardSummary>();
  /** Bodies, kept only for what has actually been opened this session. */
  private readonly bodies = new Map<string, Dashboard>();
  /** In-flight body fetches, so a hover-prefetch and the click that follows it
   *  share one request rather than racing two. */
  private readonly inflight = new Map<string, Promise<Dashboard | undefined>>();
  private port?: MessagePort;
  private loaded = false;
  /** Pending until the roster's first snapshot — the stream ends its replay with an
   *  explicit completion marker, so even an empty roster arrives. */
  private state: StreamHealth = 'pending';

  constructor(
    private readonly backend: BackendClient,
    private readonly gatewayId: string,
  ) {
    super();
  }

  get all(): DashboardSummary[] {
    // Newest first: the one someone just edited is the one they want next.
    return [...this.cache.values()].sort((a, b) => b.updatedAtMs - a.updatedAtMs);
  }

  get(id: string): DashboardSummary | undefined {
    return this.cache.get(id);
  }

  /** The gateway this manager is scoped to. */
  get gateway(): string {
    return this.gatewayId;
  }

  async start(): Promise<void> {
    this.cache.clear();
    this.loaded = false;
    this.state = 'pending';
    this.dispatchEvent(new CustomEvent('change'));
    this.port = await this.backend.api.subscribeDashboards(this.gatewayId);
    this.port.onmessage = (e: MessageEvent<StreamFrame<DashboardSummary>>) => this.onFrame(e.data);
    this.port.start();
  }

  stop(): void {
    this.port?.postMessage({ kind: 'close' });
    this.port = undefined;
    this.cache.clear();
    this.bodies.clear();
    this.inflight.clear();
    this.loaded = false;
    this.state = 'pending';
  }

  /** Whether this roster has delivered — part of what a gateway's badge claims. */
  get health(): StreamHealth {
    return this.state;
  }

  private onFrame(f: StreamFrame<DashboardSummary>): void {
    if (f.kind === 'error') {
      // Stream broke — KEEP the roster (a blip must not blank the list for the
      // reconnect window; the next snapshot replaces it wholesale), but stop
      // vouching for it. Ignoring this frame is how the badge stayed green.
      this.state = 'down';
      this.dispatchEvent(new CustomEvent('change'));
      return;
    }
    if (f.kind !== 'snapshot') return;
    this.state = 'live';
    const previous = this.cache;
    this.cache = new Map();
    for (const s of f.items) {
      this.cache.set(s.id, s);
      /*
       * A body whose digest MOVED was edited elsewhere; drop it so the next
       * open re-reads rather than rendering a colleague's edit as though it
       * were current, indefinitely, with nothing on screen to say so.
       *
       * Compared against the ROSTER's own previous digest rather than against
       * the body's. Those two are computed by the same server function and
       * agree in practice — but a manager that depends on two sources agreeing
       * re-fetches forever the day they do not, once per roster frame, which is
       * a load a stale-cache bug has no business generating. One source cannot
       * disagree with itself.
       */
      const before = previous.get(s.id);
      if (before && before.digest !== s.digest) this.bodies.delete(s.id);
    }
    for (const id of [...this.bodies.keys()]) if (!this.cache.has(id)) this.bodies.delete(id);
    this.loaded = true;
    this.dispatchEvent(new CustomEvent('change'));
  }

  /** True once a snapshot has arrived - so an empty roster reads as empty rather
   *  than as still-arriving, which are different things to a person. */
  get ready(): boolean {
    return this.loaded;
  }

  /**
   * Warm a body without rendering it, for a hover that has not become a click.
   *
   * Deliberately the SAME call the page makes, so a prefetch that lands first
   * turns the click into a cache read and one that does not costs nothing - the
   * page's own request finds the work already in flight rather than starting a
   * second one. Failures are swallowed: a speculative fetch that misses must
   * never surface as an error the user did not ask for.
   */
  prefetchBody(id: string): void {
    void this.body(id).catch(() => {
      /* speculative */
    });
  }

  /**
   * The full dashboard, tiles included. Resolves undefined for an id the
   * gateway does not have, which a detail page renders as "not found" rather
   * than as a blank canvas.
   */
  async body(id: string): Promise<Dashboard | undefined> {
    const held = this.bodies.get(id);
    if (held) return held;
    const running = this.inflight.get(id);
    if (running) return running;

    const p = (async () => {
      const r = await this.backend.api.getDashboard(this.gatewayId, id);
      /*
       * unavailable → THROW, missing → undefined. The distinction is the whole
       * fix for the cold deep-link that said "this board no longer exists"
       * while the transport was still connecting: a caller that treats every
       * falsy answer as absence needs the two cases to arrive differently, and
       * a throw is the one shape a speculative prefetch already swallows.
       */
      if (r.kind === 'unavailable') throw new Error('relay unavailable');
      if (r.kind === 'missing') return undefined;
      const wire = r.board;
      // SPREAD, not listed — the same rule as `toWire` below, for the same reason, on the way IN.
      // This was a field-by-field copy too, and it dropped the background on every read: the
      // assistant set one, the save carried it, the read threw it away, the board never showed it,
      // and the next save of what was read wrote the board back without it. A field the wire
      // carries travels by default; the one derived field is the only thing stated.
      const board: Dashboard = { ...wire, tileCount: wire.tiles.length };
      this.bodies.set(id, board);
      return board;
    })().finally(() => this.inflight.delete(id));

    this.inflight.set(id, p);
    return p;
  }

  /**
   * Create a board. THE METERED CALL — it can be refused at the org's dashboard
   * ceiling, and that refusal is a sentence rather than a throw so a caller can
   * show it. Editing is `save`, which is never metered: being at your limit
   * must not stop you fixing what you already have.
   */
  async create(board: Dashboard, via = ''): Promise<{ ok: boolean; error: string; retryable?: boolean; entitlement?: boolean }> {
    return this.backend.api.createDashboard(this.gatewayId, this.toWire(board), via);
  }

  /**
   * Replace a board. `baseDigest` is the digest this client last saw; the
   * gateway refuses a save whose base has moved rather than merging it, because
   * last-write-wins on a board a colleague is also editing loses their work
   * without telling anyone.
   */
  /*
   * Add ONE widget as a new tile to an existing board — the marketplace "download
   * onto a dashboard" path, and the same read-body → append → save the AI's
   * dashboards.update uses (only APPENDING instead of replacing the tile list).
   * The tile lands at the bottom of the grid at the catalog's default size, so it
   * never overlaps what is already there. `via` attributes the write ('' = the
   * user; the AI passes its own).
   */
  async addTile(
    dashboardId: string,
    widget: { source: string; config: Record<string, unknown>; title?: string; id?: string },
    via = '',
  ): Promise<{ ok: boolean; error: string }> {
    const current = await this.body(dashboardId);
    if (!current) return { ok: false, error: 'Could not read that dashboard.' };
    const entry = catalogForSource(widget.source);
    const w = Math.max(6, entry?.minW ?? 4);
    const h = Math.max(4, entry?.minH ?? 3);
    // Bottom of the current grid — the new tile stacks below, never overlapping.
    const bottom = current.tiles.reduce((m, t) => Math.max(m, t.layout.y + t.layout.h), 0);
    // The id is a NAME, never the source: a sandbox widget's source is its whole program, and a
    // tile id built from it was the program slugified. The listing id names it; a native tile's
    // source is a name already; anything else falls back to its title.
    const name = widget.id || (widget.source.startsWith('native:') ? widget.source : widget.title) || 'widget';
    const slug = name.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'widget';
    const tile: DashboardTile = {
      id: `${slug}-${current.tiles.length + 1}-${Math.round(current.updatedAtMs % 100000)}`,
      source: widget.source,
      config: { ...(entry?.defaults ?? {}), ...widget.config },
      layout: { x: 0, y: bottom, w, h },
      title: widget.title,
    };
    const board: Dashboard = { ...current, tiles: [...current.tiles, tile] };
    return this.save(board, current.digest, via);
  }

  async save(board: Dashboard, baseDigest: string, via = ''): Promise<{ ok: boolean; error: string }> {
    const r = await this.backend.api.saveDashboard(this.gatewayId, this.toWire(board), baseDigest, via);
    // The body we hold is now stale whatever happened: on success the digest
    // moved, on a conflict ours was never what the gateway had.
    if (r.ok) this.bodies.delete(board.id);
    return r;
  }

  /** Share a board: the gateway freezes it and mints the credential. */
  async share(dashboardId: string, terms: ShareTermsWire, scope: ShareScopeWire) {
    return this.backend.api.shareDashboard(this.gatewayId, dashboardId, terms, scope);
  }
  async revokeShare(shareId: string, email = ''): Promise<{ ok: boolean; error: string }> {
    return this.backend.api.revokeShare(this.gatewayId, shareId, email);
  }
  async listShares(dashboardId: string): Promise<ShareListingWire[]> {
    return this.backend.api.listShares(this.gatewayId, dashboardId);
  }

  async delete(id: string): Promise<{ ok: boolean; error: string }> {
    const r = await this.backend.api.deleteDashboard(this.gatewayId, id);
    if (r.ok) this.bodies.delete(id);
    return r;
  }

  /**
   * Search this gateway's symbol catalog. Lives here rather than in a manager
   * of its own because the only caller is dashboard authoring: a tile has to
   * name a symbol, and a caller that cannot look one up can only guess.
   */
  async searchSymbols(
    controllerId: string,
    fts: string,
    limit: number,
  ): Promise<{ path: string; dataType: string; permission: string; childCount: number }[]> {
    const page = await this.backend.api.fetchSymbolPage(this.gatewayId, controllerId, { fts, pageSize: limit });
    return page.rows.map((r) => ({ path: r.path, dataType: r.dataType, permission: r.permission, childCount: r.childCount }));
  }

  /*
   * A SECOND FIELD-BY-FIELD COPY, and therefore a second place a field can be dropped.
   *
   * This narrows the domain board to what `DashboardBodyWire` carries — and it does it by listing
   * the fields, so anything added to a board and not added here is silently deleted by the next
   * save. The background found that out: it survived every spread-based edit path in this file and
   * died here, on a list nobody thought to update.
   *
   * Spread rather than enumerated now, with the derived field removed explicitly. The two shapes
   * differ by exactly one thing (`tileCount`, which the wire recomputes), so stating the difference
   * is both shorter and the version that cannot go stale — a field added to a board travels by
   * default instead of by being remembered.
   */
  private toWire(board: Dashboard) {
    const { tileCount: _derived, ...wire } = board;
    return { ...wire, description: board.description ?? '' };
  }
}
